import { createCursorListResponseSchema, cursorPaginationInputSchema, withCursorPaginationExclusivity } from "./util/cursorPagination.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/kaisuuken.ts
const kaisuukenSourceSchema = z.enum([
	"event",
	"admin",
	"quest",
	"promotion"
]);
const kaisuukenConsumeLogActionTypeSchema = z.enum([
	"consumed",
	"refunded",
	"revoked",
	"expired"
]);
const templateI18nFieldsSchema = z.object({
	categoryName: z.string(),
	templateName: z.string(),
	templateDescription: z.string(),
	templateRuleDescription: z.string()
});
const templateDetailSchema = z.object({
	categoryCode: z.string(),
	templateId: z.uuidv7(),
	templateCode: z.string(),
	taskTypes: z.array(z.string()),
	routeTo: z.string().nullable(),
	routeToNative: z.string().nullable(),
	icon: z.string().nullable()
}).extend(templateI18nFieldsSchema.shape);
const kaisuukenDetailSchema = z.object({
	id: z.uuidv7(),
	source: kaisuukenSourceSchema,
	availableAt: z.iso.datetime().nullable(),
	expiresAt: z.iso.datetime().nullable()
}).extend(templateDetailSchema.shape);
const listInputSchema = z.object({ ids: z.array(z.uuidv7()).min(1).max(100) });
const listOutputSchema = z.object({ data: z.array(kaisuukenDetailSchema) });
const searchInputSchema = withCursorPaginationExclusivity(cursorPaginationInputSchema.extend({
	templateId: z.uuidv7().optional(),
	categoryCode: z.string().optional()
}));
const searchOutputSchema = createCursorListResponseSchema(kaisuukenDetailSchema);
const summaryGroupItemSchema = z.object({
	count: z.number().nonnegative(),
	soonestExpireAt: z.iso.datetime().nullable(),
	expiryCounts: z.array(z.object({
		expiresAt: z.iso.datetime().nullable(),
		count: z.number().nonnegative()
	}))
}).extend(templateDetailSchema.shape);
const summaryOutputSchema = z.object({ kaisuukens: z.array(summaryGroupItemSchema) });
const templateListInputSchema = z.object({ ids: z.array(z.uuidv7()).min(1).max(100) });
const templateListOutputSchema = z.object({ data: z.array(templateDetailSchema) });
const logItemSchema = z.object({
	id: z.uuidv7(),
	kaisuukenId: z.uuidv7(),
	templateCode: z.string(),
	categoryCode: z.string(),
	templateName: z.string().nullable(),
	taskType: z.string().nullable(),
	taskId: z.string().nullable(),
	action: kaisuukenConsumeLogActionTypeSchema,
	creditCost: z.number().int().nullable(),
	createdAt: z.iso.datetime()
});
const logsInputSchema = withCursorPaginationExclusivity(cursorPaginationInputSchema.extend({
	action: kaisuukenConsumeLogActionTypeSchema.optional(),
	templateCode: z.string().optional(),
	categoryCode: z.string().optional()
}));
const logsOutputSchema = createCursorListResponseSchema(logItemSchema);
const checkMatchingInputSchema = z.discriminatedUnion("type", [z.object({
	type: z.literal("generation-task"),
	parameters: z.record(z.string(), z.unknown())
}), z.object({
	type: z.literal("training-task"),
	baseModelId: z.string()
})]);
const checkMatchingOutputSchema = z.object({
	matches: z.array(z.object({
		templateId: z.uuidv7(),
		kaisuukens: z.array(z.object({
			id: z.uuidv7(),
			expiresAt: z.iso.datetime().nullable()
		})).max(3),
		total: z.number()
	})),
	total: z.number()
});
const kaisuukenContract = oc.prefix("/kaisuuken").tag("Kaisuuken").router({
	list: oc.route({
		summary: "List kaisuuken cards by IDs for the current user",
		method: "POST",
		path: "/list"
	}).input(listInputSchema).output(listOutputSchema).errors({ UNAUTHORIZED: {
		status: 401,
		message: "User not authenticated"
	} }),
	search: oc.route({
		summary: "Search kaisuuken cards for the current user with filters and pagination",
		method: "GET",
		path: "/search"
	}).input(searchInputSchema).output(searchOutputSchema).errors({ UNAUTHORIZED: {
		status: 401,
		message: "User not authenticated"
	} }),
	summary: oc.route({
		summary: "List summary of kaisuuken for the current user",
		method: "GET",
		path: "/summary"
	}).output(summaryOutputSchema).errors({ UNAUTHORIZED: {
		status: 401,
		message: "User not authenticated"
	} }),
	check: oc.route({
		summary: "Check which kaisuuken match the given parameters. Returns up to 3 cards per template.",
		method: "POST",
		path: "/check"
	}).input(checkMatchingInputSchema).output(checkMatchingOutputSchema).errors({
		UNAUTHORIZED: {
			status: 401,
			message: "User not authenticated"
		},
		INCORRECT_PARAMETERS: {
			status: 400,
			message: "The provided parameters are incorrect or incomplete"
		}
	}),
	templateList: oc.route({
		summary: "List kaisuuken templates by IDs",
		method: "POST",
		path: "/template/list"
	}).input(templateListInputSchema).output(templateListOutputSchema),
	logs: oc.route({
		summary: "List consume logs for the current user with pagination",
		method: "GET",
		path: "/logs"
	}).input(logsInputSchema).output(logsOutputSchema).errors({ UNAUTHORIZED: {
		status: 401,
		message: "User not authenticated"
	} })
});
//#endregion
export { kaisuukenContract, kaisuukenDetailSchema, templateDetailSchema, templateI18nFieldsSchema };
